import React, { useContext } from "react";
import PropTypes from "prop-types";
import { classes } from "../../../../../util/style-utils";
import { useNavigate } from "react-router-dom";
import { GeaoContext } from "../../../GeaoContextProvider";
import useDisabledPages from "../../../hooks/useDisabledPages";
import GeaoFooter from "./GeaoFooter";

export default function GeaoVerticalStepperLayout({
    children,
    header,
    subHeader,
    verticalStepperElements,
    currentStepperItem
}) {
    const { agentInterview } = useContext(GeaoContext);
    const interviewIsCompleted = agentInterview?.outcome === "Complete";

    const navigate = useNavigate();
    const disabledPages = useDisabledPages();

    const enableAllSteps = agentInterview == null && process.env.NODE_ENV === "development";

    const stepperItems = verticalStepperElements?.flatMap(element => element.items)?.map(item => item.text);
    const currentStepperItemIndex = stepperItems?.indexOf(currentStepperItem);

    function getStepperElementState(element) {
        if (interviewIsCompleted) {
            return "completed";
        }

        const firstItemIndex = stepperItems?.indexOf(element.items?.at(0)?.text);
        const lastItemIndex = stepperItems?.indexOf(element.items?.at(-1)?.text);

        if (lastItemIndex < currentStepperItemIndex) {
            return "completed";
        }

        if (firstItemIndex <= currentStepperItemIndex && lastItemIndex >= currentStepperItemIndex) {
            return "current";
        }

        if (firstItemIndex > currentStepperItemIndex) {
            return "future";
        }

        return null;
    }

    function getStepperItemState(item) {
        if (interviewIsCompleted || enableAllSteps) {
            return "completed";
        }

        const itemIndex = stepperItems?.indexOf(item.text);

        if (disabledPages.includes(item.path)) {
            return "future";
        }

        if (itemIndex < currentStepperItemIndex) {
            return "completed";
        }

        if (itemIndex === currentStepperItemIndex) {
            return "current";
        }

        if (itemIndex > currentStepperItemIndex) {
            return "future";
        }

        return null;
    }

    return (
        <main id="wrapper" role="main" >
            <a data-onboarding-link-id="shared:Layout:GeaoVerticalStepperLayout:SkipToMainContent" className="skip-to-content" href="#wrapper">Skip to main content</a>
            <header id="primary-header" role="banner">
                <div className="header-logo">
                    <a data-onboarding-link-id="shared:Layout:GeaoVerticalStepperLayout:Home" href="/">
                        <img className="companyLogo pl-2" data-tenant-branding-logo="true" src="https://aadcdn.msftauthimages.net/dbd5a2dd-m7upushe63t5mro-qoncxx7g0ffd-q0du4joa4qyehc/logintenantbranding/0/bannerlogo?ts=638435352267202102" alt="Company Logo" />
                    </a>
                </div>
            </header>
            <div className="content vertical-stepper-layout">
                <gds-drawer remove-expand-collapse-action={true} expanded={true} side="left">
                    {verticalStepperElements?.map(element => (
                        <gds-vertical-stepper key={element.title} state={getStepperElementState(element)} expanded={getStepperElementState(element) !== "future"}>
                            <span slot="title">{element.title}</span>
                            {element.items?.map(item => {
                                const state = getStepperItemState(item);
                                return state === "future" ?
                                    <gds-vertical-stepper-item key={item.text} state={state}>
                                        <span>{item.text}</span>
                                    </gds-vertical-stepper-item>
                                    :
                                    <gds-vertical-stepper-item data-onboarding-button-id="shared:Layout:GeaoVerticalStepperLayout:NavigateToStep" data-onboarding-option-id={item.trackingId} key={item.text} state={state} onClick={() => { navigate(item.path); }}>
                                        <span>{item.text}</span>
                                    </gds-vertical-stepper-item>
                            })}
                        </gds-vertical-stepper>
                    ))}
                </gds-drawer>
                <div className="content">
                    <div className="page-header--wrapper">
                        <div className="container m-0">
                            <div className={classes("page-header", !subHeader && "page-header--without-subheader")}>
                                <div className="page-header-left">
                                    {header && <h2>{header}</h2>}
                                    {subHeader && <h4>{subHeader}</h4>}
                                </div>
                            </div>
                        </div>
                    </div>

                    <div className="margin-top" style={{ width: "750px", marginLeft: "49px", overflow: "visible" }}>
                        {children}
                    </div>
                </div>
            </div>
            <GeaoFooter />
        </main>
    );
}

GeaoVerticalStepperLayout.propTypes = {
    children: PropTypes.node,
    header: PropTypes.oneOfType([PropTypes.string, PropTypes.node]),
    subHeader: PropTypes.oneOfType([PropTypes.string, PropTypes.node]),
    verticalStepperElements: PropTypes.arrayOf(PropTypes.shape({
        title: PropTypes.string.isRequired,
        items: PropTypes.arrayOf(PropTypes.shape({
            text: PropTypes.string.isRequired,
            trackingId: PropTypes.string,
            path: PropTypes.string
        }))
    })),
    currentStepperItem: PropTypes.string
};